import { createRouter } from '@ionic/vue-router';
import { createWebHashHistory, RouteRecordRaw } from 'vue-router';
import { LINKS } from '@/utils/links';
import {
  clearXStateFlow,
  getActiveXStateFlow,
  isXStateFlowActive,
} from '@/composables/useXStateFlow';
import { isLoggedIn } from '@/services/auth';
import { generateFlowRoutes } from '@/parcours/router/flowRoutes';
import { trackPageView } from '@/composables/useAnalytics';
const routes: RouteRecordRaw[] = [
  ...generateFlowRoutes(),
  {
    path: '/',
    redirect: LINKS.AUTH.SIGN_IN,
  },
  {
    path: '/tabs/',
    component: () => import('@/components/TabsLayout.vue'),
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/pages/DashboardPage.vue'),
      },
      {
        path: 'dashboard/my-appointment',
        name: 'myAppointmentInTabs',
        component: () => import('@/appointments/pages/MyAppointmentsPage.vue'),
      },
      {
        path: 'dashboard/appointments/:appointmentId',
        name: 'Demande de soin',
        component: () => import('@/appointments/pages/AppointmentPage.vue'),
      },
      {
        path: 'dashboard/appointments/:appointmentId/assignation',
        name: 'Demande de soin - assignation',
        component: () => import('@/appointments/pages/AppointmentOfferPage.vue'),
      },
      {
        path: 'dashboard/appointments/:appointmentId/assignation/decline',
        name: 'Demande de soin - Refus assignation',
        component: () => import('@/appointments/pages/DeclineAppointmentPage.vue'),
      },
      {
        path: 'dashboard/appointments/:appointmentId/assignation/accepted',
        name: 'Demande de soin - Accepté',
        component: () => import('@/appointments/pages/AcceptedAppointmentAssignationPage.vue'),
      },
      {
        path: 'dashboard/appointments/:appointmentId/assignation/slots',
        name: 'Demande de soin - Créneaux',
        component: () => import('@/appointments/pages/AppointmentSlotListPage.vue'),
      },
      {
        path: 'dashboard/appointments/:appointmentId/edit-frequency',
        name: 'Demande de soin - Modification de la fréquence',
        component: () => import('@/appointments/pages/EditAppointmentFrequencyPage.vue'),
      },
      {
        path: 'dashboard/appointments/:appointmentId/edit-frequency-rule/:frequencyRuleId',
        name: 'Demande de soin - Modification de la fréquence - 2',
        component: () => import('@/appointments/pages/EditAppointmentFrequencyRulePage.vue'),
      },
      {
        path: 'dashboard/appointments/:appointmentId/edit-datetime',
        name: 'Demande de soin - Modification de la date et heure',
        component: () => import('@/appointments/pages/EditAppointmentDateTimePage.vue'),
      },
      {
        path: 'dashboard/appointments/:appointmentId/end-screen',
        name: 'Demande de soin - End screen',
        component: () => import('@/appointments/organisms/CloseAppointmentEndScreen.vue'),
      },
      {
        path: 'infomed',
        name: 'InfoMed',
        component: () => import('@/pages/InfoMed/InfoMedPage.vue'),
      },
      {
        path: 'infomed/label/:labelId',
        name: 'CoursePage',
        component: () => import('@/pages/InfoMed/Label/Course/CoursePage.vue'),
      },
      {
        path: 'infomed/label/:labelId/quiz',
        name: 'QuizPage',
        component: () => import('@/pages/InfoMed/Label/Course/Quiz/QuizPage.vue'),
      },
      {
        path: 'infomed/label/:labelId/quiz/question',
        name: 'QuizQuestion',
        component: () => import('@/pages/InfoMed/Label/Course/Quiz/QuizQuestion.vue'),
      },
      {
        path: 'infomed/label/:labelId/quiz/quizValidated',
        name: 'quizValidated',
        component: () => import('@/pages/InfoMed/Label/Course/Quiz/QuizValidated.vue'),
      },
      {
        path: 'infomed/label/:labelId/quiz/quizNotValidated',
        name: 'quizNotValidated',
        component: () => import('@/pages/InfoMed/Label/Course/Quiz/QuizNotValidated.vue'),
      },
      {
        path: 'infomed/label/:labelId/modules/:moduleId',
        name: 'ModulePage',
        component: () => import('@/pages/InfoMed/Label/Course/Module/ModulePage.vue'),
      },
      {
        path: 'infomed/ressources/:id',
        name: 'RessourceDetail',
        component: () => import('@/pages/InfoMed/ProductSheetPage/ProductSheetPageDetailPage.vue'),
      },
      {
        path: 'offers',
        name: 'Offers',
        component: () => import('@/offers/pages/OffersPage.vue'),
      },
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/pages/Profile/ProfilPage.vue'),
      },
      {
        path: 'profile/charte-libheros',
        name: 'CharteLibheros',
        component: () => import('@/pages/Profile/CharteLibheros.vue'),
      },
      {
        path: 'profile/labels',
        name: 'LabelList',
        component: () => import('@/pages/Profile/LabelList.vue'),
      },
      {
        path: 'profile/contact',
        name: 'Contacter',
        component: () => import('@/pages/Profile/Contact.vue'),
      },
      {
        path: 'profile/cares',
        name: 'ProfileCares',
        component: () => import('@/pages/Profile/ProfileCares.vue'),
      },
      {
        path: 'profile/cover-area',
        name: 'ProfileCoverArea',
        component: () => import('@/pages/Profile/ProfileCoverArea.vue'),
      },
      {
        path: 'profile/colleagues',
        name: 'Colleagues',
        component: () => import('@/pages/Profile/ProfileColleagues.vue'),
      },
      {
        path: 'profile/schedule',
        name: 'OfficeSchedule',
        component: () => import('@/pages/Profile/OfficeSchedule.vue'),
      },
      {
        path: 'profile/schedule/add-slot',
        name: 'OfficeScheduleAddSlot',
        component: () => import('@/pages/Profile/OfficeScheduleAddSlot.vue'),
      },
      {
        path: 'profile/visibility',
        name: 'OfficeVisibility',
        component: () => import('@/pages/Profile/OfficeVisibility.vue'),
      },
      {
        path: 'profile/sponsorship',
        name: 'ProfileSponsorship',
        component: () => import('@/pages/Profile/ProfileSponsorship.vue'),
      },
      {
        path: 'profile/detail',
        name: 'ProfileDynamicDetail',
        component: () => import('@/pages/Profile/ProfileDynamicDetail.vue'),
      },
      {
        path: 'profile/flow/detail',
        name: 'ProfileFlowDetail',
        component: () => import('@/pages/Profile/ProfileDynamicDetail.vue'),
      },
      {
        path: 'profile/flow/form',
        name: 'ProfileFlowForm',
        component: () => import('@/pages/Profile/ProfileForm.vue'),
      },
      {
        path: 'profile/invoice',
        name: 'Invoice',
        component: () => import('@/pages/InvoiceMain.vue'),
      },
      {
        path: 'profile/invoice/flow/detail',
        name: 'InvoiceDetail',
        component: () => import('@/pages/Invoice/InvoiceDetail.vue'),
      },
      {
        path: 'profile/invoice/flow/pro-info',
        name: 'InvoiceProInfo',
        component: () => import('@/pages/Invoice/ProInfo/ReadOrUpdateInfo.vue'),
      },
      {
        path: 'profile/invoice/flow/pro-info-edit',
        name: 'InvoiceProInfoEdit',
        component: () => import('@/pages/Invoice/ProInfo/DynamicInfoForm.vue'),
      },
      {
        path: 'profile/invoice/flow/preview',
        name: 'InvoiceViewerPage',
        component: () => import('@/pages/InvoiceViewerPage.vue'),
      },
      {
        path: 'profile/invoice/flow/otp-validation',
        name: 'InvoiceYouSignOtpValidation',
        component: () => import('@/pages/Invoice/InvoiceYouSignOtpValidation.vue'),
      },
      {
        path: 'profile/invoice/flow/end',
        name: 'InvoiceEndFlow',
        component: () => import('@/pages/Invoice/InvoiceEndFlow.vue'),
      },
      {
        path: 'profile/invoice/historic-flow/viewer',
        name: 'HistoricInvoiceViewer',
        component: () => import('@/pages/Invoice/HistoricInvoiceViewerPage.vue'),
      },
      // Routes tutoriel hébergées dans le contexte tabs pour éviter que
      // TabsLayout soit démonté/restauré depuis le cache (source du bug de navigation).
      {
        path: 'tutorial-appointment',
        name: 'TutorialAppointment',
        component: () => import('@/atoms/tutorial/TutorialAppointmentPage.vue'),
      },
      {
        path: 'tutorial-select-slots',
        name: 'TutorialSelectSlots',
        component: () => import('@/atoms/tutorial/TutorialSelectSlotsPage.vue'),
      },
      {
        path: 'tutorial-mission-overview',
        name: 'TutorialMissionOverview',
        component: () => import('@/atoms/tutorial/TutorialMissionOverviewPage.vue'),
      },
      {
        path: 'telecare/phone',
        name: 'TelecarePhone',
        component: () => import('@/pages/Telecare/InsertPhone.vue'),
      },
      {
        path: 'telecare/patients',
        name: 'TelecarePatients',
        component: () => import('@/pages/Telecare/SelectPatient.vue'),
      },
    ],
  },
  {
    path: LINKS.NOTIFICATION,
    name: 'Notification',
    component: () => import('@/pages/Notification/NotificationPage.vue'),
  },
  {
    path: `${LINKS.NOTIFICATION}/:id`,
    name: 'NotificationDetail',
    component: () => import('@/pages/Notification/NotificationDetailPage.vue'),
  },
  {
    path: '/sandbox',
    name: 'Sandbox',
    component: () => import('@/pages/SandboxPage.vue'),
  },
  {
    path: '/sandbox-generic',
    name: 'SandboxGeneric',
    component: () => import('@/pages/sandbox-generic/GenericFileViewSandbox.vue'),
  },
  {
    path: LINKS.AUTH.SIGN_UP.PRO_STEP,
    name: 'Informations du pro',
    component: () => import('@/pages/auth/signup/01-pro/SignupProStep.vue'),
  },
  {
    path: LINKS.AUTH.SIGN_UP.JOB_STEP,
    name: 'Informations du cabinet',
    component: () => import('@/pages/auth/signup/02-job/SignupJobStep.vue'),
  },
  {
    path: LINKS.AUTH.SIGN_UP.AREA_STEP,
    name: 'Zone de tournée',
    component: () => import('@/pages/auth/signup/03-area/SignupAreaStep.vue'),
  },
  {
    path: LINKS.AUTH.SIGN_UP.CARES_STEP,
    name: 'Choix des soins',
    component: () => import('@/pages/auth/signup/04-cares/SignupCaresStep.vue'),
  },
  {
    path: '/auth/signin',
    name: 'Connexion',
    component: () => import('@/pages/auth/SignInPage.vue'),
  },
  {
    path: LINKS.AUTH.RESET_PASSWORD.CONFIRM_EMAIL,
    name: 'Reset password 1',
    component: () => import('@/pages/auth/reset-password/ResetPasswordConfirmEmailStep.vue'),
  },
  {
    path: LINKS.AUTH.RESET_PASSWORD.CONFIRM_EMAIL_OTP,
    name: 'Reset password 2',
    component: () => import('@/pages/auth/reset-password/ResetPasswordConfirmEmailOtpStep.vue'),
  },
  {
    path: LINKS.AUTH.RESET_PASSWORD.INPUT,
    name: 'Reset password 3',
    component: () => import('@/pages/auth/reset-password/ResetPasswordInputStep.vue'),
  },
  {
    path: LINKS.MISSIONS.BASE,
    component: () => import('@/missions/templates/MissionTemplate.vue'),
    children: [
      {
        path: LINKS.MISSIONS.ID(':missionId'),
        name: 'Details mission',
        component: () => import('@/missions/pages/MissionOverviewPage.vue'),
      },
      {
        path: LINKS.MISSIONS.SELECT_SLOTS(':missionId'),
        name: 'Sélection de créneaux',
        component: () => import('@/missions/pages/SelectMissionSlotsPage.vue'),
      },
      {
        path: LINKS.MISSIONS.SELECTED_SLOTS_CONFIRMATION,
        name: 'Validation des créneaux',
        component: () => import('@/missions/pages/SelectedSlotsConfirmationPage.vue'),
      },
      {
        path: LINKS.MISSIONS.DECLINE(':missionId'),
        name: 'Refuser la mission',
        component: () => import('@/missions/pages/DeclineMissionPage.vue'),
      },
      {
        path: LINKS.MISSIONS.SLOT.ID(':missionId', ':slotId'),
        name: 'Créneau - Mission',
        component: () => import('@/missions/pages/MissionSlotOverviewPage.vue'),
      },
      {
        path: LINKS.MISSIONS.SLOT.LIST(':missionId'),
        name: 'Tous mes créneaux',
        component: () => import('@/missions/pages/AcceptedMissionSlotListPage.vue'),
      },
      {
        path: LINKS.MISSIONS.END,
        name: 'mission',
        component: () => import('@/missions/pages/CloseMissionEndScreen.vue'),
      },
      {
        path: '/missions/:missionId/protocol',
        name: 'Protocole de mission',
        component: () => import('@/missions/pages/MissionProtocolPage.vue'),
      },
    ],
  },
  {
    path: LINKS.MISSIONS.SLOT.PATIENTS(':missionId', ':slotId'),
    name: 'Créneau',
    component: () => import('@/missions/pages/MissionSlotPatientListPage.vue'),
  },
  {
    path: LINKS.PATIENT.DETAILS.ID(':patientId'),
    name: 'Informations personnelles',
    component: () => import('@/patients/pages/PatientPersonalDetailsPage.vue'),
  },
  {
    path: LINKS.PATIENT.DETAILS.NAME(':patientId'),
    name: 'Informations personnelles - Prénom / Nom de famille',
    component: () => import('@/patients/pages/PatientFullnamePage.vue'),
  },
  {
    path: LINKS.PATIENT.DETAILS.PHONE(':patientId'),
    name: 'Informations personnelles - Numéro de téléphone',
    component: () => import('@/patients/pages/PatientPhonePage.vue'),
  },
  {
    path: LINKS.PATIENT.DETAILS.EMAIL(':patientId'),
    name: 'Informations personnelles - Email',
    component: () => import('@/patients/pages/PatientEmailPage.vue'),
  },
  {
    path: LINKS.PATIENT.DETAILS.BIRTH_DATE(':patientId'),
    name: 'Informations personnelles - Date de naissance',
    component: () => import('@/patients/pages/PatientBirthdatePage.vue'),
  },
  {
    path: LINKS.PATIENT.DETAILS.GENDER(':patientId'),
    name: 'Informations personnelles - Genre',
    component: () => import('@/patients/pages/PatientGenderPage.vue'),
  },
  {
    path: LINKS.SCREENING.FLOW,
    component: () => import('@/pages/Screening/ScreeningFlowContainer.vue'),
    children: [
      {
        path: 'pre-filter',
        name: 'ScreeningPreFilter',
        component: () => import('@/pages/Screening/steps/ScreeningPreFilter.vue'),
      },
      {
        path: 'tool-list',
        name: 'ScreeningToolList',
        component: () => import('@/pages/Screening/steps/ScreeningToolList.vue'),
      },
      {
        path: 'patient-info',
        name: 'ScreeningPatientInfo',
        component: () => import('@/pages/Screening/steps/ScreeningPatientInfo.vue'),
      },
      {
        path: 'questionnaire',
        name: 'ScreeningQuestionnaire',
        component: () => import('@/pages/Screening/steps/ScreeningQuestionnaire.vue'),
      },
      {
        path: 'results',
        name: 'ScreeningResults',
        component: () => import('@/pages/Screening/steps/ScreeningResults.vue'),
      },
    ],
  },
  {
    path: LINKS.ERROR,
    name: 'Error',
    component: () => import('@/pages/ErrorPage.vue'),
  },
  // ── Tutorial pages (fake data, no API calls) ────────────────────────────────
  {
    path: '/tutorial/appointment',
    component: () => import('@/atoms/tutorial/TutorialAppointmentPage.vue'),
  },
  {
    path: '/tutorial/select-slots',
    component: () => import('@/atoms/tutorial/TutorialSelectSlotsPage.vue'),
  },
  {
    path: '/tutorial/mission-overview',
    component: () => import('@/atoms/tutorial/TutorialMissionOverviewPage.vue'),
  },
  {
    path: '/tutorial/infomed',
    component: () => import('@/atoms/tutorial/TutorialInfoMedLabelsPage.vue'),
  },
  {
    path: '/tutorial/invoice',
    component: () => import('@/atoms/tutorial/TutorialInvoiceMainPage.vue'),
  },
  {
    path: '/tutorial/invoice/detail',
    component: () => import('@/atoms/tutorial/TutorialInvoiceDetailPage.vue'),
  },
  {
    path: '/tutorial/invoice/preview',
    component: () => import('@/atoms/tutorial/TutorialInvoicePreviewPage.vue'),
  },
];

const router = createRouter({
  history: createWebHashHistory(import.meta.env.BASE_URL),
  routes,
});

const PUBLIC_PATHS = [LINKS.AUTH.SIGN_IN, '/auth'];

let _cachedLoggedIn: boolean | null = null;
let _cacheExpiry = 0;

async function getCachedLoginState(): Promise<boolean> {
  const now = Date.now();
  if (_cachedLoggedIn !== null && now < _cacheExpiry) {
    return _cachedLoggedIn;
  }
  _cachedLoggedIn = await isLoggedIn();
  _cacheExpiry = now + 2000; // cache 2s pour éviter les appels répétés sur switch de tabs
  return _cachedLoggedIn;
}

export function invalidateLoginCache(): void {
  _cachedLoggedIn = null;
  _cacheExpiry = 0;
}

router.beforeEach(async (to) => {
  const loggedIn = await getCachedLoginState();

  if (to.path === LINKS.AUTH.SIGN_IN && loggedIn) {
    return LINKS.TABS.DASHBOARD;
  }

  const isPublic = PUBLIC_PATHS.some((p) => to.path.startsWith(p));
  if (!loggedIn && !isPublic) {
    return LINKS.AUTH.SIGN_IN;
  }

  if (to.path.startsWith(LINKS.SCREENING.FLOW + '/') && !isXStateFlowActive()) {
    return LINKS.TABS.DASHBOARD;
  }
});

router.afterEach(async (to, from: any) => {
  // Don't track same-route navigations
  if (to.fullPath === from.fullPath) {
    return;
  }

  await trackPageView(to.fullPath);

  const xstateFlow = getActiveXStateFlow();
  if (xstateFlow) {
    const routePaths = Object.values(xstateFlow.config.stepRouteMap);
    if (!routePaths.includes(to.path)) {
      clearXStateFlow();
    }
  }
});

export default router;
